import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, RefreshCw, CheckCircle, XCircle, AlertCircle, ExternalLink, Send, Loader2 } from 'lucide-react';
import { api } from '../../context/AuthContext';

const VERDICT_CONFIG = {
  PASS:    { label: 'Indexed',     color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-950/30', icon: CheckCircle },
  FAIL:    { label: 'Not Indexed', color: 'text-red-600 dark:text-red-400',         bg: 'bg-red-50 dark:bg-red-950/30',         icon: XCircle },
  NEUTRAL: { label: 'Neutral',     color: 'text-slate-600 dark:text-slate-400',     bg: 'bg-slate-50 dark:bg-slate-900',         icon: AlertCircle },
  ERROR:   { label: 'Error',       color: 'text-amber-600 dark:text-amber-400',     bg: 'bg-amber-50 dark:bg-amber-950/30',      icon: AlertCircle },
  UNKNOWN: { label: 'Unknown',     color: 'text-slate-500 dark:text-slate-400',     bg: 'bg-slate-50 dark:bg-slate-900',         icon: AlertCircle },
};

export default function IndexationPage() {
  const [summary, setSummary] = useState(null);
  const [urlInput, setUrlInput] = useState('');
  const [checkLoading, setCheckLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [checkResults, setCheckResults] = useState([]);
  const [error, setError] = useState('');
  const [submitResults, setSubmitResults] = useState([]);
  const [activeTab, setActiveTab] = useState('check');

  const loadSummary = async () => {
    try {
      const { data } = await api.get('/indexation/summary');
      if (data.success) setSummary(data);
    } catch { /* silent */ }
  };

  useEffect(() => { loadSummary(); }, []);

  const handleCheck = async (e) => {
    e.preventDefault();
    setCheckLoading(true);
    setError('');
    setCheckResults([]);
    try {
      const urls = urlInput.split('\n').map((u) => u.trim()).filter(Boolean);
      const { data } = await api.post('/indexation/check', { urls });
      if (data.success) {
        setCheckResults(data.results || []);
        await loadSummary();
        setActiveTab('results');
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : err.message || 'Check failed');
    } finally {
      setCheckLoading(false);
    }
  };

  const handleSubmit = async () => {
    const urls = urlInput.split('\n').map((u) => u.trim()).filter(Boolean);
    if (urls.length === 0) return;
    setSubmitLoading(true);
    setSubmitResults([]);
    setError('');
    try {
      const { data } = await api.post('/indexation/submit', { urls });
      setSubmitResults(data.results || []);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : err.message || 'Submission failed');
    } finally {
      setSubmitLoading(false);
    }
  };

  const VerdictIcon = (verdict) => {
    const cfg = VERDICT_CONFIG[verdict] || VERDICT_CONFIG.UNKNOWN;
    return cfg.icon;
  };

  return (
    <motion.div
      className="space-y-6 p-4 md:p-6"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
          <Search className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Google Indexed Pages Check</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-0.5 text-sm">
            Verify which pages are indexed by Google using the URL Inspection API and submit URLs for re-crawling.
          </p>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Checked', value: summary.total_checked },
            { label: 'Indexed', value: summary.indexed, color: 'text-emerald-600 dark:text-emerald-400' },
            { label: 'Not Indexed', value: summary.not_indexed, color: 'text-red-600 dark:text-red-400' },
            { label: 'Index Rate', value: `${summary.index_rate}%`, color: 'text-blue-600 dark:text-blue-400' },
          ].map((s) => (
            <div key={s.label} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 text-center shadow-sm">
              <p className={`text-2xl font-bold ${s.color || 'text-slate-900 dark:text-white'}`}>{s.value}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 dark:border-slate-800">
        {['check', 'results', 'history'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 capitalize transition-colors ${
              activeTab === tab
                ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
            }`}
          >
            {tab === 'results' ? `Results (${checkResults.length})` : tab === 'history' ? `History (${summary?.total_checked || 0})` : 'Check URLs'}
          </button>
        ))}
      </div>

      {/* Check tab */}
      {activeTab === 'check' && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
              URLs to check (one per line, max 50)
            </label>
            <textarea
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder={`https://yoursite.com/\nhttps://yoursite.com/about\nhttps://yoursite.com/services`}
              rows={6}
              className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm resize-y font-mono"
            />
            <p className="text-xs text-slate-400 mt-1">Leave empty to auto-check tracked pages from on-page scans.</p>
          </div>

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

          {(() => {
            const allIndexed = checkResults.length > 0 && checkResults.every((r) => r.verdict === 'PASS');
            return (
              <div className="flex flex-col gap-2">
                <div className="flex gap-3">
                  <button
                    onClick={handleCheck}
                    disabled={checkLoading}
                    className="flex items-center gap-2 bg-emerald-600 text-white px-5 py-2 rounded-md text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {checkLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    {checkLoading ? 'Checking…' : 'Check Indexation'}
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={submitLoading || !urlInput.trim() || allIndexed}
                    title={allIndexed ? 'All URLs are already indexed' : undefined}
                    className="flex items-center gap-2 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 px-5 py-2 rounded-md text-sm hover:bg-slate-50 dark:hover:bg-slate-900 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {submitLoading ? 'Submitting…' : 'Submit for Indexing'}
                  </button>
                </div>
                {allIndexed && (
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                    <CheckCircle className="w-3.5 h-3.5" />
                    All URLs are already indexed — no submission needed.
                  </p>
                )}
              </div>
            );
          })()}

          {submitResults.length > 0 && (
            <div className="space-y-1 mt-2">
              {/* Show reconnect banner if any result has scope_missing */}
              {submitResults.some((r) => r.status === 'scope_missing') && (
                <div className="flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3 text-xs text-amber-800 dark:text-amber-300">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />
                  <div>
                    <p className="font-semibold">Google Indexing API permission required</p>
                    <p className="mt-0.5">Your connected Google account was authorised without the Indexing API scope. Please go to <strong>Integrations</strong>, disconnect Google Search Console, and reconnect to grant the required permission.</p>
                  </div>
                </div>
              )}
              {submitResults.map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  {r.status === 'submitted'
                    ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                    : r.status === 'scope_missing'
                      ? <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
                      : <XCircle className="w-3.5 h-3.5 text-red-500" />
                  }
                  <span className="truncate text-slate-700 dark:text-slate-300">{r.url}</span>
                  <span className={
                    r.status === 'submitted' ? 'text-emerald-600 dark:text-emerald-400'
                    : r.status === 'scope_missing' ? 'text-amber-600 dark:text-amber-400'
                    : 'text-red-500'
                  }>
                    {r.status === 'scope_missing' ? 'permission needed' : r.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Results tab */}
      {activeTab === 'results' && (
        <div className="space-y-2">
          {checkResults.length === 0 ? (
            <p className="text-center text-slate-500 py-12 text-sm">Run a check to see results.</p>
          ) : (
            checkResults.map((r, i) => {
              const cfg = VERDICT_CONFIG[r.verdict] || VERDICT_CONFIG.UNKNOWN;
              const Icon = cfg.icon;
              return (
                <div key={i} className={`rounded-lg border border-slate-200 dark:border-slate-800 p-3 shadow-sm ${cfg.bg}`}>
                  <div className="flex items-start gap-3">
                    <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${cfg.color}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-slate-900 dark:text-white hover:underline truncate max-w-md">
                          {r.url}
                        </a>
                        <span className={`text-xs font-medium ${cfg.color}`}>{cfg.label}</span>
                      </div>
                      <div className="mt-1 flex flex-wrap gap-3 text-xs text-slate-500 dark:text-slate-400">
                        {r.coverage_state && <span>Coverage: {r.coverage_state}</span>}
                        {r.indexing_state && <span>Indexing: {r.indexing_state}</span>}
                        {r.last_crawl_time && <span>Last crawl: {new Date(r.last_crawl_time).toLocaleDateString()}</span>}
                        {r.crawled_as && <span>Crawled as: {r.crawled_as}</span>}
                      </div>
                      {r.google_canonical && r.google_canonical !== r.url && (
                        <p className="text-xs text-amber-600 mt-1">Google canonical: {r.google_canonical}</p>
                      )}
                      {r.error && <p className="text-xs text-red-500 mt-1">{r.error}</p>}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* History tab */}
      {activeTab === 'history' && summary?.pages && (
        <div className="space-y-2">
          {summary.pages.map((r, i) => {
            const cfg = VERDICT_CONFIG[r.verdict] || VERDICT_CONFIG.UNKNOWN;
            const Icon = cfg.icon;
            return (
              <div key={i} className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-3 shadow-sm flex items-center gap-3">
                <Icon className={`w-4 h-4 shrink-0 ${cfg.color}`} />
                <span className="text-sm text-slate-900 dark:text-white truncate flex-1">{r.url}</span>
                <span className={`text-xs ${cfg.color}`}>{cfg.label}</span>
                <span className="text-xs text-slate-400">{r.checked_at ? new Date(r.checked_at).toLocaleDateString() : ''}</span>
              </div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}
